import { GALLERY_SORTS, type GalleryCardType, type GallerySort } from "@volgo/core/contract";
import { useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router";
import {
  EMPTY_QUERY,
  fromSearchParams,
  type SearchQuery,
  toFilter,
  toggleCategory,
  toSearchParams,
} from "../search/query";
import { useSession } from "../session";
import { Button } from "../ui/Button";
import { CatalogSync } from "../ui/CatalogSync";
import { CategoryFilter } from "../ui/CategoryFilter";
import { useCardSize } from "../ui/cardSize";
import { GalleryCard } from "../ui/GalleryCard";
import { useGallerySource } from "../ui/GallerySource";
import { Icon } from "../ui/icons";
import { SORT_LABELS } from "../ui/labels";
import { Empty, ErrorNote, Loading } from "../ui/States";
import { usePaged } from "../ui/useLoad";

const PAGE_SIZE = 80;

/**
 * Search results. Every condition comes from the address (`SearchQuery`); changing a condition changes
 * the address, and the list follows. The category strip and the sort are the only conditions drawn here:
 * everything else lives in the search bar.
 */
export function BrowsePage() {
  const { client } = useSession();
  const location = useLocation();
  const navigate = useNavigate();

  const query = useMemo(() => fromSearchParams(new URLSearchParams(location.search)), [location.search]);
  const key = toSearchParams(query).toString();
  const setQuery = useCallback(
    (next: SearchQuery) => navigate({ pathname: "/", search: toSearchParams(next).toString() }),
    [navigate],
  );

  const list = usePaged<GalleryCardType>(
    (cursor) =>
      query.categories.length === 0
        ? Promise.resolve({ items: [], nextCursor: null })
        : client.call("catalog.list", {
            query: { ...toFilter(query), sort: query.sort, limit: PAGE_SIZE, ...(cursor ? { cursor } : {}) },
          }),
    key,
  );
  useGallerySource(list.items);
  const cards = useCardSize();

  return (
    <>
      <CategoryFilter selected={query.categories} onToggle={(c) => setQuery(toggleCategory(query, c))} />

      <div className="sum" tabIndex={-1}>
        <CatalogSync />
        <fieldset className="card-size" aria-label="카드 크기">
          <button
            type="button"
            aria-label="크게"
            title="크게"
            aria-pressed={cards.size === "large"}
            onClick={() => cards.choose("large")}
          >
            <Icon name="grid" small />
          </button>
          <button
            type="button"
            aria-label="보통"
            title="보통"
            aria-pressed={cards.size === "normal"}
            onClick={() => cards.choose("normal")}
          >
            <Icon name="grid3" small />
          </button>
        </fieldset>
        <select
          className="select select-bare"
          aria-label="정렬"
          value={query.sort}
          onChange={(e) => setQuery({ ...query, sort: e.target.value as GallerySort })}
        >
          {GALLERY_SORTS.map((s) => (
            <option key={s} value={s}>
              {SORT_LABELS[s]}
            </option>
          ))}
        </select>
      </div>

      <section className="grid" aria-label="검색 결과" aria-busy={list.status === "loading"}>
        {list.items.map((card) => (
          <GalleryCard
            key={card.gid}
            card={card}
            to={{ pathname: `/g/${card.gid}`, search: location.search }}
            state={{ inApp: true }}
          />
        ))}
      </section>

      {list.status === "loading" && <Loading />}
      {list.status === "error" && <ErrorNote error={list.error} onRetry={list.reload} />}
      {list.status === "ready" && list.items.length === 0 && (
        <Empty title="조건에 맞는 갤러리가 없습니다">
          <p>검색창의 토큰을 지우거나 분류를 더 켜 보세요.</p>
          {key !== "" && <Button onClick={() => setQuery(EMPTY_QUERY)}>조건 모두 지우기</Button>}
        </Empty>
      )}
      <div className="more">
        {list.status === "more" && <Loading />}
        {list.status === "more-error" && <ErrorNote error={list.error} />}
        {(list.status === "ready" || list.status === "more-error") && list.hasMore && (
          <Button onClick={list.loadMore}>↓ 80개 더 보기</Button>
        )}
      </div>
    </>
  );
}
